import styled from "styled-components";
import React from "react";
import { FilterConfig, ActiveFilters } from "../types/Filters.ts";

const Container = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  margin-top: 1rem;
  flex-wrap: wrap;
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 31, 82, 0.1);
  transition: background 0.3s ease, box-shadow 0.3s ease;
  width: 100%;
  box-sizing: border-box;

  .dark-mode & {
    background: #2a2a3e;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
`;

const StyledInput = styled.input`
  padding: 0.75rem 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  color: #001f52;
  background-color: white;

  &:focus {
    outline: none;
    border-color: #64da4c;
    box-shadow: 0 0 0 3px rgba(100, 218, 76, 0.1);
  }

  &::placeholder {
    color: #999;
  }

  .dark-mode & {
    background-color: #1f1f2e;
    border-color: #3a3a4e;
    color: #e0e0e0;

    &::placeholder {
      color: #999;
    }
  }
`;

const StyledSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  color: #001f52;
  background-color: white;
  cursor: pointer;
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: #64da4c;
    box-shadow: 0 0 0 3px rgba(100, 218, 76, 0.1);
  }

  .dark-mode & {
    background-color: #1f1f2e;
    border-color: #3a3a4e;
    color: #e0e0e0;
  }
`;

const FilterLabel = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: #001f52;
  margin-bottom: 0.5rem;
  display: block;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #e0e0e0;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const MultiSelectContainer = styled.div<{ $wide?: boolean }>`
  position: relative;
  min-width: ${props => props.$wide ? '250px' : '150px'};
`;

const MultiSelectButton = styled.button<{ $isOpen?: boolean }>`
  padding: 0.75rem 1rem;
  padding-right: 2.5rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  color: #001f52;
  background-color: white;
  cursor: pointer;
  width: 100%;
  text-align: left;
  position: relative;
  appearance: none;

  &::after {
    content: '';
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%) ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 6px solid #001f52;
    transition: transform 0.2s ease;
    pointer-events: none;
  }

  &:focus {
    outline: none;
    border-color: #64da4c;
    box-shadow: 0 0 0 3px rgba(100, 218, 76, 0.1);
  }

  .dark-mode & {
    background-color: #1f1f2e;
    border-color: #3a3a4e;
    color: #e0e0e0;

    &::after {
      border-top-color: #e0e0e0;
    }
  }
`;

const MultiSelectDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 0.25rem;
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  padding: 0.5rem;

  .dark-mode & {
    background: #1f1f2e;
    border-color: #3a3a4e;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
  }

  .dark-mode & {
    &:hover {
      background-color: #3a3a4e;
    }
  }
`;

const CheckboxInput = styled.input`
  cursor: pointer;
  width: 18px;
  height: 18px;
  min-width: 18px;
  min-height: 18px;
  flex: 0 0 18px;
  margin: 0;
  accent-color: #64da4c;
`;

const ClearButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: #f5f5f5;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.95rem;
  cursor: pointer;
  color: #001f52;
  transition: all 0.2s ease;
  margin-left: auto;
  align-self: flex-end;

  &:hover {
    background: #e0e0e0;
    border-color: #64da4c;
  }

  .dark-mode & {
    background: #1f1f2e;
    border-color: #3a3a4e;
    color: #e0e0e0;

    &:hover {
      background: #3a3a4e;
      border-color: #64da4c;
    }
  }
`;

interface Props<T> {
  configs: FilterConfig<T>[];
  value: ActiveFilters<T>;
  onChange: (filters: ActiveFilters<T>) => void;
  onClear?: () => void;
  showClearButton?: boolean;
  extraButtons?: React.ReactNode;
}

export default function Filters<T>({
  configs,
  value,
  onChange,
  onClear,
  showClearButton = false,
  extraButtons,
}: Props<T>) {
  const [openMultiSelects, setOpenMultiSelects] = React.useState<Set<string>>(new Set());

  const toggleMultiSelect = (key: string) => {
    const newSet = new Set(openMultiSelects);
    if (newSet.has(key)) {
      newSet.delete(key);
    } else {
      newSet.clear();
      newSet.add(key);
    }
    setOpenMultiSelects(newSet);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.multi-select-container')) {
        setOpenMultiSelects(new Set());
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Container>
      {configs.map((config) => {
        const filterValue = value[config.key];

        const update = (val: any) =>
          onChange({ ...value, [config.key]: val || undefined });

        switch (config.type) {
          case "text":
            return (
              <StyledInput
                key={String(config.key)}
                placeholder={config.label}
                value={filterValue ?? ""}
                onChange={(e) => update(e.target.value)}
              />
            );

          case "number":
            return (
              <StyledInput
                key={String(config.key)}
                type="number"
                placeholder={config.label}
                value={filterValue ?? ""}
                onChange={(e) => update(e.target.value)}
              />
            );

          case "date":
            return (
              <StyledInput
                key={String(config.key)}
                type="date"
                value={filterValue ?? ""}
                onChange={(e) => update(e.target.value)}
              />
            );

          case "select":
            const selectValue = filterValue ?? config.defaultValue ?? "";
            const showDefaultOption = config.defaultValue === undefined;
            return (
              <FilterGroup key={String(config.key)}>
                <FilterLabel htmlFor={`filter-${String(config.key)}`}>
                  {config.label}
                </FilterLabel>
                <StyledSelect
                  id={`filter-${String(config.key)}`}
                  value={selectValue}
                  onChange={(e) => update(e.target.value)}
                >
                  {showDefaultOption && <option value="">Alle</option>}
                  {config.options?.map((opt, index) => (
                    <option key={`${String(config.key)}-${String(opt.value)}-${index}`} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </StyledSelect>
              </FilterGroup>
            );

          case "multiselect":
            const selectedValues = Array.isArray(filterValue) ? filterValue : [];
            const isOpen = openMultiSelects.has(String(config.key));
            const selectedCount = selectedValues.length;
            const displayText = selectedCount === 0 
              ? "Alle" 
              : selectedCount === 1 
                ? `${selectedCount} valgt`
                : `${selectedCount} valgte`;

            return (
              <FilterGroup key={String(config.key)} className="multi-select-container">
                <FilterLabel htmlFor={`filter-${String(config.key)}`}>
                  {config.label}
                </FilterLabel>
                <MultiSelectContainer $wide={config.wide}>
                  <MultiSelectButton
                    type="button"
                    $isOpen={isOpen}
                    onClick={() => toggleMultiSelect(String(config.key))}
                  >
                    {displayText}
                  </MultiSelectButton>
                  {isOpen && (
                    <MultiSelectDropdown>
                      {config.options?.map((opt, index) => {
                        const isChecked = selectedValues.includes(opt.value);
                        return (
                          <CheckboxLabel key={`${String(config.key)}-${String(opt.value)}-${index}`}>
                            <CheckboxInput
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                const newValues = e.target.checked
                                  ? [...selectedValues, opt.value]
                                  : selectedValues.filter((v) => v !== opt.value);
                                update(newValues.length > 0 ? newValues : undefined);
                              }}
                            />
                            <span>{opt.label}</span>
                          </CheckboxLabel>
                        );
                      })}
                    </MultiSelectDropdown>
                  )}
                </MultiSelectContainer>
              </FilterGroup>
            );

          default:
            return null;
        }
      })}
      {showClearButton && onClear && (
        <ClearButton onClick={onClear}>
          Ryd filtre
        </ClearButton>
      )}
      {extraButtons}
    </Container>
  );
}
